import { getBassRomanDegreeInMainKey } from './chordToNotes'
import { getBassDegreeForChord } from './riemannFunctions'

function normalizeDegreeValue(value) {
  if (!value) return null
  const normalized = value.toString().trim().toUpperCase()
  return normalized || null
}

function getChordRootDegree(chord) {
  if (!chord) return null
  if (chord.degree) return normalizeDegreeValue(chord.degree)
  if (chord.root) return normalizeDegreeValue(chord.root)
  if (chord.displayLabel) {
    const match = chord.displayLabel.match(/^[♭#]?([ivx]+)/i)?.[1]
    return match ? match.toUpperCase() : null
  }
  return null
}

function getExplicitPedalDegree(chord) {
  if (!chord || chord.pedalBlocked === true) return null
  return normalizeDegreeValue(chord.pedalDegree)
}

function normalizeFigureForBass(figureOrInversion) {
  if (figureOrInversion == null) return undefined
  const s = String(figureOrInversion).trim()
  if (s === '6/4' || s === '64') return '64'
  return s || undefined
}

/**
 * @param {object|null} chord
 * @param {{ tonality?: string, mode?: string }} [options] - si tonality fournie, basse réelle en tonalité principale (secondaires, emprunts) ; sinon logique historique
 */
function getBassDegree(chord, options = {}) {
  if (!chord) return null
  const explicitPedal = getExplicitPedalDegree(chord)
  if (explicitPedal) return explicitPedal
  const tonality = options.tonality != null && String(options.tonality).trim() !== '' ? String(options.tonality).trim() : null
  const mode = options.mode === 'minor' ? 'minor' : 'major'
  if (tonality) {
    const fromPitch = getBassRomanDegreeInMainKey(chord, tonality, mode)
    if (fromPitch) return fromPitch
  }
  const root = getChordRootDegree(chord)
  if (!root) return null
  const figure = normalizeFigureForBass(chord.figure) ?? normalizeFigureForBass(chord.inversion) ?? chord.figure ?? chord.inversion
  return getBassDegreeForChord(root, figure)
}

export function computeEffectivePedalDegrees(chords = [], options = {}) {
  const effective = new Array(chords.length).fill(null)
  if (!Array.isArray(chords) || chords.length === 0) return effective

  const explicit = chords.map(getExplicitPedalDegree)
  const blocked = chords.map((chord) => chord?.pedalBlocked === true)
  const bass = chords.map((c) => getBassDegree(c, options))

  for (let i = 0; i < chords.length; i++) {
    if (!explicit[i]) continue
    const degree = explicit[i]
    effective[i] = degree

    for (let left = i - 1; left >= 0; left--) {
      if (blocked[left] || explicit[left]) break
      if (bass[left] !== degree) break
      effective[left] = degree
    }

    for (let right = i + 1; right < chords.length; right++) {
      if (blocked[right] || explicit[right]) break
      if (bass[right] !== degree) break
      effective[right] = degree
    }
  }

  return effective
}

export function computePedalGroups(chords = [], options = {}) {
  const effectiveDegrees = computeEffectivePedalDegrees(chords, options)
  const groups = []
  let start = null
  let currentDegree = null

  for (let i = 0; i < effectiveDegrees.length; i++) {
    const degree = effectiveDegrees[i]
    if (degree && degree === currentDegree) {
      continue
    }
    if (start !== null) {
      groups.push({ startIndex: start, endIndex: i - 1, degree: currentDegree })
    }
    if (degree) {
      start = i
      currentDegree = degree
    } else {
      start = null
      currentDegree = null
    }
  }

  if (start !== null) {
    groups.push({ startIndex: start, endIndex: effectiveDegrees.length - 1, degree: currentDegree })
  }

  return { groups, effectiveDegrees }
}

export function getImplicitPedalDegree(chords = [], index, options = {}) {
  if (!Array.isArray(chords) || index == null || index < 0 || index >= chords.length) return null
  const chord = chords[index]
  if (!chord || chord.pedalBlocked === true) return null
  if (getExplicitPedalDegree(chord)) return null
  const effective = computeEffectivePedalDegrees(chords, options)[index]
  return effective || null
}
